<script lang="ts">import StickerComingSoon from "./images/sticker-coming-soon.png";
import data from "@emoji-mart/data";
import { cfgGiphyApiKey, FeatureSet, ReleaseStage, stateFeatureSet, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { SearchIcon } from "@infinitro/icons";
import { Text } from "@infinitro/text";
import { createEventDispatcher, onMount } from "svelte";
const dispatch = createEventDispatcher();
/** Which tab to open first (0 = emojis, 1 = GIFs). Remount with `{#key}` when switching entry buttons. */
export let initialTab = 0;
/** `chat` = dark panel (default). `light` = white card for support-chat and other light shells. */
export let variant = "chat";
/** When false, the stickers tab is never shown (even in Preview). */
export let enableStickersTab = true;
function handleEmojiSelect(emoji) {
	console.log("handleEmojiSelect", emoji);
	dispatch("emoji-selected", emoji.skins[0].native);
}
function handleGifSelect(gifUrl) {
	dispatch("gif-selected", gifUrl);
}
function handleStickerSelect(sticker) {
	dispatch("sticker-selected", sticker);
}
const emojiData = data;
const GIF_DEBOUNCE_MS = 350;
let search = "";
let recent = [];
let filteredEmojis = {};
let selectedChatMediaTab = initialTab;
let gifDebounceId = undefined;
let gifSearchToken = 0;
let giphyResults = [];
let searchQuery = "";
let apiKey = cfgGiphyApiKey();
$: chatMediaTabs = [$t(Verbiage.chat_media_emojis_tab), $t(Verbiage.chat_media_gifs_tab)];
$: if (selectedChatMediaTab === 0) {
	filteredEmojis = searchEmojis(search);
} else if (selectedChatMediaTab === 1 && search.trim().length > 0) {
	searchQuery = search;
	searchGifs();
}
function searchEmojis(query) {
	if (!query.trim()) {
		const groups = {};
		for (const category of emojiData.categories) {
			groups[category.id] = category.emojis.map((id) => emojiData.emojis[id]).filter(Boolean);
		}
		return groups;
	}
	const q = query.toLowerCase();
	const results = Object.values(emojiData.emojis).filter((emoji) => {
		return emoji.id.toLowerCase().includes(q) || emoji.name.toLowerCase().includes(q) || emoji.keywords.some((k) => k.toLowerCase().includes(q)) || emoji.emoticons && emoji.emoticons.some((e) => e.toLowerCase().includes(q)) || Object.keys(emojiData.aliases).some((alias) => alias.toLowerCase().includes(q) && emojiData.aliases[alias] === emoji.id);
	});
	return { Results: results };
}
function selectEmoji(emoji) {
	if (!recent.includes(emoji.id)) {
		recent = [emoji.id, ...recent].slice(0, 10);
		localStorage.setItem("RECENT_EMOJIS", JSON.stringify(recent));
	}
	handleEmojiSelect(emoji);
}
const selectTab = (index) => {
	selectedChatMediaTab = index;
	search = "";
	giphyResults = [];
	if (index === 1) {
		searchQuery = "a";
		fetchGifs();
	}
};
async function fetchGifs() {
	const query = searchQuery;
	const myToken = ++gifSearchToken;
	const data = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${query}&limit=50`).then((r) => r.json()).catch(() => ({ data: [] }));
	if (myToken !== gifSearchToken || searchQuery.trim().length === 0) return;
	giphyResults = data.data ?? [];
}
async function selectGif(gifUrl) {
	handleGifSelect(gifUrl);
}
function searchGifs() {
	const q = searchQuery.trim();
	if (gifDebounceId) clearTimeout(gifDebounceId);
	if (q.length === 0) {
		giphyResults = [];
		return;
	}
	gifDebounceId = setTimeout(() => {
		fetchGifs();
	}, GIF_DEBOUNCE_MS);
}
onMount(() => {
	selectTab(initialTab);
	const stored = localStorage.getItem("RECENT_EMOJIS");
	if (stored) {
		try {
			recent = JSON.parse(stored);
		} catch {
			recent = [];
		}
	}
	if (enableStickersTab && wantAtLeastReleaseStage(ReleaseStage.Alpha) && $stateFeatureSet === FeatureSet.Preview) {
		chatMediaTabs = [
			$t(Verbiage.chat_media_emojis_tab),
			$t(Verbiage.chat_media_gifs_tab),
			$t(Verbiage.chat_media_stickers_tab)
		];
	}
});
</script>

<div class="emoji-picker" class:emoji-picker-light={variant === 'light'}>
	<div class="tabs">
		{#each chatMediaTabs as tab, index (index + '-' + selectedChatMediaTab)}
			<button
				class="tab"
				class:tab-active={index === selectedChatMediaTab}
				on:click={() => selectTab(index)}
			>
				<Text
					fontFamily="main"
					textColor={variant === 'light'
						? index === selectedChatMediaTab
							? 'secondary'
							: 'gray'
						: index === selectedChatMediaTab
							? 'white'
							: 'gray'}
					customStyle="font-size: 14px;"
				>
					{tab}
				</Text>
			</button>
		{/each}
	</div>

	{#if selectedChatMediaTab !== 2}
		<div class="emoji-search-wrapper">
			<div class="search-icon">
				<SearchIcon />
			</div>
			<input
				class="emoji-search"
				type="text"
				bind:value={search}
				placeholder={selectedChatMediaTab === 0
					? $t(Verbiage.chat_search_emojis_placeholder)
					: $t(Verbiage.chat_search_gifs_placeholder)}
			/>
		</div>
	{/if}

	{#if selectedChatMediaTab === 0}
		<div class="emoji-scroll">
			{#if recent.length > 0}
				<div class="emoji-group">
					<Text
						fontFamily="main"
						customStyle="text-transform: capitalize; font-size: 12px;"
						textColor="gray"
					>
						{$t(Verbiage.chat_emojis_recents_category)}
					</Text>
					<div class="emoji-list">
						{#each recent as id}
							{#if emojiData.emojis[id]}
								<button class="emoji-btn" on:click={() => selectEmoji(emojiData.emojis[id])}>
									{emojiData.emojis[id].skins[0].native}
								</button>
							{/if}
						{/each}
					</div>
				</div>
			{/if}

			{#each Object.entries(filteredEmojis) as [group, emojis]}
				{#if emojis.length > 0}
					<div class="emoji-group">
						<Text
							fontFamily="main"
							customStyle="text-transform: capitalize; font-size: 12px;"
							textColor="gray"
						>
							{$t(Verbiage[`chat_emojis_${group.toLowerCase()}_category`]) || group}
						</Text>
						<div class="emoji-list">
							{#each emojis as emoji}
								<button class="emoji-btn" on:click={() => selectEmoji(emoji)}>
									{emoji.skins[0].native}
								</button>
							{/each}
						</div>
					</div>
				{/if}
			{/each}
		</div>
	{:else if selectedChatMediaTab === 1}
		{#if giphyResults.length !== 0}
			<div class="gif-list">
				{#each giphyResults as gif, index (index)}
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
					<img
						src={gif.images.fixed_height_small.url}
						alt={gif.title}
						on:click={() => selectGif(gif.images.fixed_height_small.url)}
						class="gif-thumbnail"
					/>
				{/each}
			</div>
		{/if}
	{:else if selectedChatMediaTab === 2}
		<div class="stickers-container">
			<div class="coming-soon">
				<div class="coming-soon-illustration">
					<img src={StickerComingSoon} alt="Sticker coming soon" width="75" />
				</div>

				<div class="coming-soon-text">
					<Text
						type="body"
						bodyWeight="extrabold"
						fontFamily="main"
						textColor="gray"
						customStyle="font-size: 16px;"
						textAlign="center"
					>
						Coming soon...
					</Text>
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.emoji-picker {
		display: flex;
		max-height: 237px;
		min-height: 237px;
		width: 100%;
		flex-direction: column;
		border-radius: 0.5rem;
		padding: 0.5rem;
		background: var(--gradient-main);
}

	.emoji-search-wrapper {
		margin-bottom: 0.5rem;
		box-sizing: border-box;
		display: flex;
		height: 38px;
		width: 100%;
		flex-shrink: 0;
		flex-direction: row;
		align-items: center;
		gap: 0.25rem;
		border-radius: 0.25rem;
		border-width: 1px;
		--tw-border-opacity: 1;
		border-color: rgb(63 75 121 / var(--tw-border-opacity, 1));
		background-image: linear-gradient(to bottom, var(--tw-gradient-stops));
		--tw-gradient-from: #181F45 var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(24 31 69 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: #090E2B var(--tw-gradient-to-position);
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		--tw-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 20px 25px -5px var(--tw-shadow-color), 0 8px 10px -6px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}

	.emoji-search {
		flex: 1 1 0%;
		border-style: none;
		background-color: transparent;
		font-size: 0.875rem;
		line-height: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.emoji-search::-moz-placeholder {
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		opacity: 0.8;
}

	.emoji-search::placeholder {
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		opacity: 0.8;
}

	.emoji-search:focus {
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	.search-icon {
		height: 1rem;
		width: 1rem;
		flex-shrink: 0;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
}

	.emoji-scroll {
		flex: 1 1 0%;
		overflow-y: auto;
		padding-right: 0.25rem;
}

	.emoji-group {
		margin-bottom: 1rem;
}

	.emoji-group h3 {
		margin-bottom: 0.5rem;
		font-size: 14px;
		font-weight: 600;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}

	.emoji-list {
		display: flex;
		width: 100%;
		flex-wrap: wrap;
		gap: 3px;
}

	.emoji-btn {
		cursor: pointer;
		border-style: none;
		background-image: none;
		font-size: 20px;
		transition-property: transform;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 100ms;
}

	.emoji-btn:hover {
		--tw-scale-x: 1.25;
		--tw-scale-y: 1.25;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.tabs {
		margin-bottom: 0.5rem;
		display: flex;
		width: 100%;
		align-items: center;
}

	.tab {
		margin-right: 0.5rem;
		display: flex;
		align-items: center;
}

	.gif-thumbnail {
		cursor: pointer;
		border-radius: 0.5rem;
		width: 100%;
		height: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
}

	.gif-list {
		display: grid;
		max-height: 237px;
		gap: 0.5rem;
		overflow-y: auto;
		padding-right: 0.25rem;
		grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
		grid-auto-rows: 80px;
}

	.stickers-container {
		display: flex;
		height: 237px;
		width: 100%;
		align-items: center;
		justify-content: center;
}

	.coming-soon {
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
}

	.coming-soon-illustration {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center;
}

	.coming-soon-text {
		margin-top: 0.5rem;
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center;
}

	.emoji-picker-light {
		border-radius: 18px;
		border-width: 1px;
		--tw-bg-opacity: 1;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1));
		padding: 0.75rem;
		min-height: 220px;
		max-height: 220px;
		border-color: color-mix(in srgb, var(--color-text-900) 7%, var(--color-text-100));
		box-shadow:
			0 1px 2px rgba(var(--color-background-900-rgb), 0.04),
			0 4px 14px rgba(var(--color-background-900-rgb), 0.06);
		background: #ffffff;
}

	/* `@infinitro/text` gray is white@60% for dark chat shells — remap for light surfaces. */
	.emoji-picker-light :global(.custom-text-gray) {
		color: var(--color-text-500);
		opacity: 1;
	}

	.emoji-picker-light .emoji-search-wrapper {
		margin-bottom: 0.5rem;
		height: 36px;
		border-radius: 12px;
		border-width: 1px;
		background-color: var(--color-text-100);
		padding-left: 0.625rem;
		padding-right: 0.625rem;
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		border-color: color-mix(in srgb, var(--color-text-900) 10%, var(--color-text-100));
		background-image: none;
}

	.emoji-picker-light .emoji-search {
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-900);
}

	.emoji-picker-light .emoji-search::-moz-placeholder {
		color: var(--color-text-500);
}

	.emoji-picker-light .emoji-search::placeholder {
		color: var(--color-text-500);
}

	.emoji-picker-light .search-icon {
		color: var(--color-text-500);
	}

	.emoji-picker-light .tabs {
		margin-bottom: 0.625rem;
		gap: 0.25rem;
}

	.emoji-picker-light .tab {
		margin-right: 0px;
		border-radius: 9999px;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
}

	.emoji-picker-light .tab-active {
		background-color: color-mix(in srgb, var(--color-secondary-500) 14%, #ffffff);
	}

	.emoji-picker-light .emoji-btn:hover {
		--tw-scale-x: 1.1;
		--tw-scale-y: 1.1;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		border-radius: 0.375rem;
		background-color: color-mix(in srgb, var(--color-secondary-500) 10%, #ffffff);
}

	.emoji-picker-light .gif-list {
		max-height: 148px;
	}

	.emoji-picker-light .gif-thumbnail {
		border-radius: 10px;
		border-width: 1px;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
		border-color: color-mix(in srgb, var(--color-text-900) 8%, var(--color-text-100));
}

	.emoji-picker-light .gif-thumbnail:hover {
		border-color: var(--color-secondary-500);
	}

	.emoji-picker-light .emoji-scroll {
		padding-right: 0.125rem;
}

	.emoji-picker-light .emoji-scroll::-webkit-scrollbar {
		width: 4px;
	}

	.emoji-picker-light .emoji-scroll::-webkit-scrollbar-thumb {
		background-color: rgba(var(--color-background-900-rgb), 0.18);
		border-radius: 999px;
	}

	.emoji-picker-light .gif-list::-webkit-scrollbar {
		width: 4px;
	}

	.emoji-picker-light .gif-list::-webkit-scrollbar-thumb {
		background-color: rgba(var(--color-background-900-rgb), 0.18);
		border-radius: 999px;
	}
</style>
